import Debug from 'debug'
import { reportNonFatalError } from '@/app/error-handling';
import { FunctionKeys } from 'utility-types'

const debug = Debug('state-machine');

interface State<TName, TEvents> {
  name: TName
  stateEvents: TEvents | null
  enter?: () => void
  exit?: () => void
}

export type StateChangedListener<TState> = (state: TState) => void

// parameteres for a state function
type StateDelegateParameters<TState extends object, K extends StateKey<TState>> = FunctionParameters<StateDelegate<TState, K>>

// if extends function, return parameters
type FunctionParameters<T> =
      T extends (...args: any) => any ? Parameters<T> : never

// extract function member of type T based on name identified by K
type ExtractFunction<T, K extends keyof any> = T extends {
  [P in K]: (...args: any[]) => any;
} ? T[K] : never

// picks a member of a state using the specified key
type StateDelegate<TState extends object, K extends StateKey<TState>> = ExtractFunction<TState, K>

// represents all usable key of a state
type StateKey<T extends object> = T extends object ? FunctionKeys<T> : never

export class StateMachine<TState extends State<TName, unknown>, TName> {

  private currentState: TState | null = null;
  private stateChangedEventListeners:
    { token: number, listener: StateChangedListener<TName> }[] = []
  private eventListenerToken = 0

  constructor(
    private name: string) {
  }

  changeState<XState extends TState>(state: XState,
                                     events: XState extends State<unknown, infer X> ? X : never) {

    debug(`(${this.name}) changing state from '${this.currentStateName}' to '${state.name}'`)

    if (this.currentState) {
      if (this.currentState.exit) {
        this.currentState.exit()
      }
      this.currentState.stateEvents = null
    }

    state.stateEvents = events
    this.currentState = state
    if (state.enter) {
      state.enter()
    }

    for (let listener of this.stateChangedEventListeners) {
      listener.listener(state.name)
    }
  }

  executeAction<K extends StateKey<TState>>(
    member: K,
    ...args: StateDelegateParameters<TState, K>) {
 
    if (!this.currentState) {
      debug(`(${this.name}) there is no current state, action '${member}' couldn't be executed`);
      return;
    }

    this.executeStateAction(this.currentState, member, ...args);
  }

  private executeStateAction<K extends StateKey<TState>>(state: TState,
                                                         key: K,
                                                         ...args: StateDelegateParameters<TState, K>) {

    if (this.hasMember<FunctionKeys<TState>, StateKey<TState>>(key, state)) {
      debug(`(${this.name}) executing action '${key}' in state '${state.name}'`, args);

      const member = state[key] as any
      // TODO: try to solve this at type level
      const isFunction = !!(member && member.constructor && member.call && member.apply);
      if (isFunction) {
        (member as Function).call(state, ...args)
      }
    } else {
     debug(`(${this.name}) action '${key}' is not supported in state '${state.name}'`);
    }
  }

  addStateChangedListener(listener: StateChangedListener<TName>) {
    this.eventListenerToken++
    this.stateChangedEventListeners.push({ token: this.eventListenerToken, listener: listener })
    return this.eventListenerToken
  }
  
  removeStateChangedListener(token: number) {
    const index = this.stateChangedEventListeners.findIndex(x => x.token === token)
    if (index < 0) {
      reportNonFatalError('Event change listener not found', token)
      return false
    }

    this.stateChangedEventListeners.splice(index, 1)
    return true
  }

  private hasMember<T, K extends StateKey<TState>>(member: K, state: any): state is T {
    return member in state
  }

  get currentStateName() {
    return this.currentState ? this.currentState.name : ''
  }
}
